import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Paper from '@mui/material/Paper'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'

import { ApiError, makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

type Prefs = {
  email_digest_enabled: boolean
  email_digest_hour_utc: number
  on_critical_email_enabled: boolean
  in_app_toast_enabled: boolean
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const QUERY_KEY = ['me', 'notifications', 'prefs'] as const

export default function NotificationPrefsPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const qc = useQueryClient()
  const navigate = useNavigate()

  const prefsQ = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => client.call<Prefs>('/api/v1/me/notifications/prefs'),
  })

  const [state, setState] = useState<Prefs | null>(null)
  useEffect(() => {
    if (prefsQ.data && !state) setState(prefsQ.data)
  }, [prefsQ.data, state])

  const saveMut = useMutation({
    mutationFn: (next: Prefs) =>
      client.call<Prefs>('/api/v1/me/notifications/prefs', {
        method: 'PATCH',
        body: next,
      }),
    onSuccess: (fresh) => {
      qc.setQueryData(QUERY_KEY, fresh)
      setState(fresh)
    },
  })

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 3 }}>
          {prefsQ.isLoading && <Typography>Loading…</Typography>}
          {prefsQ.isError && (
            <Alert severity="error">
              {prefsQ.error instanceof Error ? prefsQ.error.message : 'Failed to load preferences.'}
            </Alert>
          )}
          {state && (
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Control how FieldLens reaches you. Defaults are all on. In-app toasts require the
                Control Tower to be open in a browser tab; emails don't.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={state.email_digest_enabled}
                    onChange={(_, v) => setState({ ...state, email_digest_enabled: v })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Daily email digest</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Summary of exceptions raised in the last 24 hours + top 5 open high+critical items.
                    </Typography>
                  </Box>
                }
              />

              <FormControl size="small" sx={{ maxWidth: 200 }} disabled={!state.email_digest_enabled}>
                <InputLabel id="digest-hr">Digest hour (UTC)</InputLabel>
                <Select
                  labelId="digest-hr"
                  value={String(state.email_digest_hour_utc)}
                  input={<OutlinedInput label="Digest hour (UTC)" />}
                  onChange={(e: SelectChangeEvent) => setState({ ...state, email_digest_hour_utc: Number(e.target.value) })}
                >
                  {HOURS.map((h) => (
                    <MenuItem key={h} value={h}>
                      {`${String(h).padStart(2, '0')}:00 UTC`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={state.on_critical_email_enabled}
                    onChange={(_, v) => setState({ ...state, on_critical_email_enabled: v })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Email on critical</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Immediate email for any critical exception. Deduped for 15 minutes per exception so you don't get spammed on flappy events.
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={state.in_app_toast_enabled}
                    onChange={(_, v) => setState({ ...state, in_app_toast_enabled: v })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>In-app toast for critical</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Show a top-right snackbar when a critical exception arrives while the Control Tower is open.
                    </Typography>
                  </Box>
                }
              />

              {saveMut.error && (
                <Alert severity="error">
                  {saveMut.error instanceof ApiError ? saveMut.error.message : 'Save failed.'}
                </Alert>
              )}
              {saveMut.isSuccess && <Alert severity="success">Preferences saved.</Alert>}

              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1 }}>
                <Button onClick={() => navigate('/')}>Cancel</Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => saveMut.mutate(state)}
                  disabled={saveMut.isPending}
                >
                  {saveMut.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
    </Container>
  )
}
